// Cửa sổ của màn Tu Luyện: chọn nơi bế quan, chọn thời lượng Tử Quan (UI_SPEC §18.1, §4.1).
// Không cuộn, ≤ 5 khối. Bước vào Tử Quan xác nhận bằng dải hỏi, nút giữ 720ms.
import type { JSX } from 'preact';
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { remainingText } from '@i18n/time';
import { BALANCE } from '@data/balance';
import { CULTIVATION_SITES, siteDef } from '@data/content/sites';
import {
  deathSeclusionBlock,
  deathSeclusionCost,
  lifespanMax,
  siteChangeBlock,
  siteDensity,
} from '@sim/cultivation';
import { changeSite, cultivation, startDeathSeclusion } from '@state/modules/cultivation';
import { askConfirm, closeWindows } from '@state/modules/ui';
import { now } from '@state/clock';
import { TextButton } from '../../kit/TextButton';

const fmt1 = (n: number): string => formatFull(Math.round(n * 100) / 100);

export function SiteWindow(): JSX.Element {
  const c = cultivation.value;
  const locked = c.idle.seclusion.kind === 'death';
  return (
    <>
      <div class="ht-block">{t('ui.cult.site.body')}</div>
      <div class="ht-rows" data-id="site-list">
        {CULTIVATION_SITES.map((s) => {
          const here = c.idle.locationId === s.id;
          const block = siteChangeBlock(c, s.id);
          return (
            <div class="ht-statline" key={s.id} data-id={`site-${s.id}`}>
              <span>
                <b class="row-name">{t(`${s.id}.name`)}</b>
                <small>{t(`${s.id}.desc`)}</small>
              </span>
              <span class="ht-chip">
                {t('ui.cult.density', { n: fmt1(siteDensity(siteDef(s.id), c.step)) })}
              </span>
              {here ? (
                <span class="ht-chip on">{t('ui.cult.site.here')}</span>
              ) : (
                <TextButton
                  id={`site-go-${s.id}`}
                  disabled={block !== null}
                  onClick={() => {
                    if (changeSite(s.id)) closeWindows();
                  }}
                >
                  {t('ui.cult.site.go')}
                </TextButton>
              )}
            </div>
          );
        })}
      </div>
      {locked && <p class="ht-reason">{t('ui.cult.site.locked')}</p>}
    </>
  );
}

export function DeathSeclusionWindow(): JSX.Element {
  const c = cultivation.value;
  const death = c.idle.seclusion.kind === 'death' ? c.idle.seclusion : null;
  const max = lifespanMax(c);
  const floorHit = BALANCE.DEATH_SECLUSION_DURATIONS_H.some(
    (h) => deathSeclusionBlock(c, h) === 'lifespanFloor',
  );
  return (
    <>
      <div class="ht-block">{t('ui.cult.death.body')}</div>
      <div class="ht-rows" data-id="death-list">
        {BALANCE.DEATH_SECLUSION_DURATIONS_H.map((h) => {
          const y = deathSeclusionCost(c, h);
          const block = deathSeclusionBlock(c, h);
          return (
            <div class="ht-statline" key={h} data-id={`death-${h}`}>
              <span>{t('ui.cult.death.row', { n: h / 2 })}</span>
              <span class="ht-chip">{t('ui.cult.death.cost', { y: fmt1(y) })}</span>
              <TextButton
                id={`death-pick-${h}`}
                disabled={block !== null}
                onClick={() =>
                  askConfirm({
                    textKey: 'ui.cult.death.ask',
                    textParams: { n: h / 2, y: fmt1(y), left: fmt1(max - c.lifespan.age - y) },
                    confirmKey: 'ui.cult.death.confirm',
                    hold: true,
                    onConfirm: () => {
                      if (startDeathSeclusion(h)) closeWindows();
                    },
                  })
                }
              >
                {t('ui.cult.death.pick')}
              </TextButton>
            </div>
          );
        })}
      </div>
      {death?.endsAtMs && (
        <p class="ht-reason" data-id="death-active">
          {t('ui.cult.death.active', { left: remainingText(death.endsAtMs - now()) })}
        </p>
      )}
      {!death && floorHit && <p class="ht-reason">{t('ui.cult.death.floor')}</p>}
    </>
  );
}
